/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@mui/material';

import { Namespaces } from '../../i18n';
import { SupportPlanResponse } from '../../types/common/PlanSupport';
import { BoxPicker } from '../BoxPicker/BoxPicker';
import { useSupportPlanCardStyles } from './SupportPlanCard.styles';

type Props = {
  supportPlans: SupportPlanResponse[];
  displayAmount?: boolean;
  currency: string;
  selectedSupportPlanId: string;
  onChange: (supportPlanId: string) => void;
};

export const SupportPlanCard = memo<Props>(
  ({ supportPlans, selectedSupportPlanId, onChange, displayAmount = true, currency }) => {
    const classes = useSupportPlanCardStyles();
    const { t } = useTranslation([Namespaces.Common, Namespaces.CreateCluster]);

    const cards = useMemo(
      () =>
        supportPlans.map(({ name, description, id, price }) => {
          return (
            <BoxPicker.Item value={id} id={`${name}-${id}`} key={`${name}-${id}`}>
              <Card classes={{ root: classes.root }}>
                <div className={classes.container}>
                  <div>
                    <span className={classes.planName}>{name}</span>
                  </div>
                  <div className={classes.planDescription}>{description}</div>
                </div>
                {displayAmount && (
                  <div className={classes.price}>
                    <span className={classes.priceAmount}>
                      {t('format_currency.currencyShort', { value: Number(price), currency })}
                    </span>
                    <span className={classes.priceDuration}>{t(`${Namespaces.CreateCluster}:labels.per_month`)}</span>
                  </div>
                )}
              </Card>
            </BoxPicker.Item>
          );
        }),
      [
        supportPlans,
        classes.root,
        classes.container,
        classes.planName,
        classes.planDescription,
        classes.price,
        classes.priceAmount,
        classes.priceDuration,
        displayAmount,
        t,
        currency,
      ],
    );

    const handleChange = (supportPlanIds: string[]) => onChange(supportPlanIds[0]);

    return (
      <BoxPicker selected={[selectedSupportPlanId]} onChange={handleChange}>
        {cards}
      </BoxPicker>
    );
  },
);
